import * as React from "react";
import {
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  Separator,
  Section,
  Header,
  SubmenuTrigger,
  Button,
  type MenuProps,
  type MenuItemProps,
  type MenuTriggerProps,
  type PopoverProps,
  type SeparatorProps,
} from "react-aria-components";
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";

import { cn } from "./utils";

export interface DropdownMenuProps extends MenuTriggerProps {}

function DropdownMenu({ ...props }: DropdownMenuProps) {
  return <MenuTrigger data-slot="dropdown-menu" {...props} />;
}

export interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  className?: string;
}

function DropdownMenuTrigger({ children, className }: DropdownMenuTriggerProps) {
  return (
    <Button data-slot="dropdown-menu-trigger" className={cn("outline-none", className)}>
      {children}
    </Button>
  );
}

export interface DropdownMenuContentProps extends Omit<MenuProps<object>, "className" | "children"> {
  className?: string;
  sideOffset?: number;
  children?: React.ReactNode;
  /** Radix-compat: alignment */
  align?: string;
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  align: _align,
  ...props
}: DropdownMenuContentProps) {
  return (
    <Popover
      offset={sideOffset}
      className={cn(
        "bg-popover text-popover-foreground entering:animate-in exiting:animate-out exiting:fade-out-0 entering:fade-in-0 exiting:zoom-out-95 entering:zoom-in-95 placement-bottom:slide-in-from-top-2 placement-left:slide-in-from-right-2 placement-right:slide-in-from-left-2 placement-top:slide-in-from-bottom-2 min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md",
        className,
      )}
      style={{ zIndex: 9999 }}
    >
      <Menu
        data-slot="dropdown-menu-content"
        className="outline-none"
        {...props}
      />
    </Popover>
  );
}

export interface DropdownMenuGroupProps {
  children: React.ReactNode;
  className?: string;
}

function DropdownMenuGroup({ children, className }: DropdownMenuGroupProps) {
  return (
    <Section data-slot="dropdown-menu-group" className={className}>
      {children}
    </Section>
  );
}

export interface DropdownMenuItemProps extends Omit<MenuItemProps, "className" | "children"> {
  className?: string;
  inset?: boolean;
  variant?: "default" | "destructive";
  children?: React.ReactNode;
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  children,
  ...props
}: DropdownMenuItemProps) {
  return (
    <MenuItem
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "focused:bg-accent focused:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focused:bg-destructive/10 dark:data-[variant=destructive]:focused:bg-destructive/20 data-[variant=destructive]:focused:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none disabled:pointer-events-none disabled:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      textValue={typeof children === "string" ? children : props.textValue}
      {...props}
    >
      {children}
    </MenuItem>
  );
}

export interface DropdownMenuCheckboxItemProps extends Omit<MenuItemProps, "className" | "children"> {
  className?: string;
  checked?: boolean;
  children?: React.ReactNode;
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: DropdownMenuCheckboxItemProps) {
  return (
    <MenuItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "focused:bg-accent focused:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        {checked && <CheckIcon className="size-4" />}
      </span>
      {children}
    </MenuItem>
  );
}

export interface DropdownMenuRadioGroupProps {
  children: React.ReactNode;
  className?: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

function DropdownMenuRadioGroup({
  children,
  className,
  ...props
}: DropdownMenuRadioGroupProps) {
  return (
    <Section data-slot="dropdown-menu-radio-group" className={className}>
      {children}
    </Section>
  );
}

export interface DropdownMenuRadioItemProps extends Omit<MenuItemProps, "className" | "children"> {
  className?: string;
  checked?: boolean;
  children?: React.ReactNode;
}

function DropdownMenuRadioItem({
  className,
  children,
  checked,
  ...props
}: DropdownMenuRadioItemProps) {
  return (
    <MenuItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "focused:bg-accent focused:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        {checked && <CircleIcon className="size-2 fill-current" />}
      </span>
      {children}
    </MenuItem>
  );
}

export interface DropdownMenuLabelProps {
  className?: string;
  inset?: boolean;
  children: React.ReactNode;
}

function DropdownMenuLabel({
  className,
  inset,
  children,
  ...props
}: DropdownMenuLabelProps) {
  return (
    <Header
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className,
      )}
      {...props}
    >
      {children}
    </Header>
  );
}

export interface DropdownMenuSeparatorProps extends Omit<SeparatorProps, "className"> {
  className?: string;
}

function DropdownMenuSeparator({
  className,
  ...props
}: DropdownMenuSeparatorProps) {
  return (
    <Separator
      data-slot="dropdown-menu-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className,
      )}
      {...props}
    />
  );
}

export interface DropdownMenuSubProps {
  children: React.ReactNode;
}

function DropdownMenuSub({ children }: DropdownMenuSubProps) {
  return <>{children}</>;
}

export interface DropdownMenuSubTriggerProps extends Omit<MenuItemProps, "className" | "children"> {
  className?: string;
  inset?: boolean;
  children?: React.ReactNode;
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: DropdownMenuSubTriggerProps) {
  return (
    <SubmenuTrigger>
      <MenuItem
        data-slot="dropdown-menu-sub-trigger"
        data-inset={inset}
        className={cn(
          "focused:bg-accent focused:text-accent-foreground open:bg-accent open:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8",
          className,
        )}
        {...props}
      >
        {children}
        <ChevronRightIcon className="ml-auto size-4" />
      </MenuItem>
      <Popover className="min-w-[8rem]">
        <Menu className="outline-none" />
      </Popover>
    </SubmenuTrigger>
  );
}

export interface DropdownMenuSubContentProps extends Omit<MenuProps<object>, "className"> {
  className?: string;
}

function DropdownMenuSubContent({
  className,
  ...props
}: DropdownMenuSubContentProps) {
  return (
    <Popover
      className={cn(
        "bg-popover text-popover-foreground entering:animate-in exiting:animate-out exiting:fade-out-0 entering:fade-in-0 exiting:zoom-out-95 entering:zoom-in-95 placement-bottom:slide-in-from-top-2 placement-left:slide-in-from-right-2 placement-right:slide-in-from-left-2 placement-top:slide-in-from-bottom-2 z-50 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-lg",
        className,
      )}
    >
      <Menu
        data-slot="dropdown-menu-sub-content"
        className="outline-none"
        {...props}
      />
    </Popover>
  );
}

// Portal is not needed with React Aria - kept for API compatibility
function DropdownMenuPortal({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
